import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import noRows from "@app/assets/list/no-rows.svg";
import { getGuides } from "@app/scene/onboarding/store";
import { RootState } from "@app/store/rootReducer";
import loggingService from "@app/util/logging";

import styles from "./NoRows.module.scss";

const messages = defineMessages({
  noRecordsDescription: {
    id: "onboarding.guides.noRows.noRecordsDescription",
    defaultMessage: "You don’t have any Guides",
    description: "Descriptive text for the No Rows overlay for grids",
  },
  resetButton: {
    id: "onboarding.guides.noRows.resetButton",
    defaultMessage: "Reset all filters",
    description: "Title of reset filter button",
  },
  resetDescription: {
    id: "onboarding.guides.noRows.resetFilterDescription",
    defaultMessage: "You’ve applied too many filters",
    description: "Title of too many filters applied",
  },
  title: {
    id: "onboarding.guides.noRows.title",
    defaultMessage: "Nothing to see here",
    description: "Title of no data for company records",
  },
});

export interface NoRowsProps {
  onResetFilters?: () => void;
}

interface Props extends NoRowsProps {
  totalRecords: number;
}

const NoRows: React.FC<Props> = ({ onResetFilters, totalRecords }) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerHeight, setContainerHeight] = useState<number>(0);
  const noRowsImgHeight = useMemo(
    () => containerHeight / (containerHeight > 300 ? 3 : 4),
    [containerHeight]
  );

  useEffect(
    () => {
      const element = containerRef.current;
      try {
        const observer = new ResizeObserver(() =>
          setContainerHeight(+(element?.clientHeight || 0))
        );

        if (element) {
          observer.observe(element);
        }

        return () => {
          observer.disconnect();
        };
      } catch (e) {
        loggingService.error("Error in NoRows", e);
      }
    },
    // disabling because we need to re-run this effect when ref changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerRef.current]
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <img alt="No data" src={noRows} style={{ height: noRowsImgHeight }} />
      <div className={cn(styles.title, { [styles.smallTitle]: noRowsImgHeight < 250 })}>
        {intl.formatMessage(messages.title)}
      </div>

      {totalRecords ? (
        <>
          <div className={styles.description}>{intl.formatMessage(messages.resetDescription)}</div>
          <Button className={styles.resetButton} onClick={onResetFilters} type="link">
            {intl.formatMessage(messages.resetButton)}
          </Button>
        </>
      ) : (
        <div className={styles.description}>
          {intl.formatMessage(messages.noRecordsDescription)}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  totalRecords: getGuides(state).length,
});

export default connect(mapStateToProps, {})(NoRows);
